import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { OrganisationsService } from "../../services/organisationsService";
import { getOrganisations } from "../../features/org/orgSlice";
import { PageHeader } from "../components/ApplicationShell";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import StatusBadge from "../components/StatusBadge";

const getId = (organisation) => organisation?.id || organisation?._id;
const isActive = (organisation) => Number(organisation?.active ?? organisation?.enabled) === 1;
const getErrorMessage = (error) => error?.response?.data?.message || error?.data?.message || error?.message || "Organisations could not be loaded.";

const Organisations = () => {
  const dispatch = useDispatch();
  const [organisations, setOrganisations] = useState([]);
  const [pageInfo, setPageInfo] = useState({ last_page: 1, total: 0 });
  const [query, setQuery] = useState({ filters: {}, page: 0, pageSize: 10, sortBy: "name", sortOrder: "asc" });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [statusTarget, setStatusTarget] = useState(null);

  const loadOrganisations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await OrganisationsService.getAllOrganisations(query.page, query.pageSize, query.sortBy, query.sortOrder, query.filters);
      const records = response?.data || [];
      setOrganisations(records);
      setPageInfo({ last_page: Number(response?.last_page) || 1, total: Number(response?.total) || records.length });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { loadOrganisations(); }, [loadOrganisations]);

  const columns = useMemo(() => [
    { key: "name", header: "Name", filter: { type: "text" } },
    { key: "email", header: "Email", render: (organisation) => organisation.email ? <a href={`mailto:${organisation.email}`}>{organisation.email}</a> : "Not specified", sortable: false },
    { key: "telephone", header: "Telephone", sortable: false },
    { key: "address", header: "Address", accessor: (organisation) => [organisation.address, organisation.post_code].filter(Boolean).join(", ") || "Not specified", sortable: false },
    { key: "active", header: "Status", accessor: (organisation) => isActive(organisation) ? "Active" : "Inactive", render: (organisation) => isActive(organisation) ? <StatusBadge type="success">Active</StatusBadge> : <StatusBadge type="secondary">Inactive</StatusBadge>, filter: { type: "select", options: [{ label: "Active", value: "1" }, { label: "Inactive", value: "0" }] }, sortable: false },
  ], []);

  const actions = useMemo(() => [
    { label: "Disable organisation", isVisible: isActive, onClick: setStatusTarget },
    { label: "Enable organisation", isVisible: (organisation) => !isActive(organisation), onClick: setStatusTarget },
  ], []);

  const updateStatus = async () => {
    if (!statusTarget) return;
    setWorking(true);
    setError("");
    try {
      if (isActive(statusTarget)) await OrganisationsService.disableOrganisation(getId(statusTarget));
      else await OrganisationsService.enableOrganisation(getId(statusTarget));
      setStatusTarget(null);
      dispatch(getOrganisations());
      await loadOrganisations();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setWorking(false);
    }
  };

  const handleTableQuery = useCallback(({ filters, sortBy }) => {
    const sort = sortBy?.[0];
    setQuery((current) => {
      const next = { ...current, filters, page: 0, sortBy: sort?.id || "name", sortOrder: sort?.desc ? "desc" : "asc" };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, []);

  const disabling = statusTarget && isActive(statusTarget);

  return (
    <>
      <PageHeader description="View organisations and manage whether they can access Worksite Manager." title="Organisations" />
      <ResponsiveDataTable actions={actions} caption="Organisations" columns={columns} data={organisations} emptyDescription="No organisations match the selected filters."
        error={error} initialSort={{ key: "name", direction: "asc" }} loading={loading || working} manual
        onPageChange={(page) => setQuery((current) => ({ ...current, page }))} onPageSizeChange={(pageSize) => setQuery((current) => ({ ...current, page: 0, pageSize }))}
        onQueryChange={handleTableQuery} onRetry={loadOrganisations} pageCount={Math.max(pageInfo.last_page, 1)} pageIndex={query.page}
        pageSize={query.pageSize} pageSizeOptions={[10, 20, 50]} showColumnSelector={false} showCount={false} showExport={false} showSearch={false}
        title="Organisations" totalRows={pageInfo.total} />

      <Dialog actions={<><Button disabled={working} onClick={() => setStatusTarget(null)} variant="outline">Cancel</Button><Button loading={working} onClick={updateStatus} variant={disabling ? "destructive" : "primary"}>{disabling ? "Disable organisation" : "Enable organisation"}</Button></>}
        description={disabling ? "Users in this organisation will no longer be able to access the system." : "Users in this organisation will be able to access the system again."}
        onClose={() => !working && setStatusTarget(null)} open={Boolean(statusTarget)} size="small" title={disabling ? "Disable this organisation?" : "Enable this organisation?"}>
        <p>Change access for <strong>{statusTarget?.name || "this organisation"}</strong>?</p>
      </Dialog>
    </>
  );
};

export default Organisations;
